export type CollectTestCaseExpectedResultItem = {
  evalId: string;
  expectedResult: Record<string, unknown>;
};

export type CollectTestCaseListItem = {
  id: string;
  name: string;
  variables: Record<string, string>;
  expectedResults: CollectTestCaseExpectedResultItem[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveExperimentName(item: Record<string, unknown>): string {
  if (typeof item.name === 'string') {
    return item.name;
  }

  if (typeof item.label === 'string') {
    return item.label;
  }

  return String(item.id);
}

function toExpectedResults(
  value: unknown
): CollectTestCaseExpectedResultItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => {
      if (!isRecord(item) || typeof item.evalId !== 'string') {
        return null;
      }

      const expectedResult = isRecord(item.expectedResult)
        ? item.expectedResult
        : {};

      return {
        evalId: item.evalId,
        expectedResult
      } satisfies CollectTestCaseExpectedResultItem;
    })
    .filter((item): item is CollectTestCaseExpectedResultItem => item !== null);
}

function toVariables(value: unknown): Record<string, string> {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, String(entry ?? '')])
  );
}

export function normalizeDatasetExperiments(
  experiments: unknown[] | undefined
): CollectTestCaseListItem[] {
  if (!Array.isArray(experiments)) {
    return [];
  }

  return experiments
    .map(item => {
      if (!isRecord(item) || typeof item.id !== 'string') {
        return null;
      }

      return {
        id: item.id,
        name: resolveExperimentName(item),
        variables: toVariables(item.variables),
        expectedResults: toExpectedResults(item.expectedResults)
      } satisfies CollectTestCaseListItem;
    })
    .filter((item): item is CollectTestCaseListItem => item !== null);
}
